// Promo Codes page - mobile-first full-screen design

import { Building2, Eye, Paintbrush, Store } from "lucide-react";

import { BoostRewardsSection } from "@/components/boosts";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import {
  AffiliateLinkCard,
  OfferStatsDisplay,
  PaymentsTab,
  PayoutTierDetails,
  PromoCodeCard,
  ShareOfferDialog,
} from "@/components/promo-codes";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import {
  isLinkOffer,
  isPromoOffer,
  useAffiliateOfferQuery,
  type AffiliateOffer,
} from "@/lib/queries/promo-codes";
import {
  Button,
  Card,
  CardContent,
  Separator,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import {
  createFileRoute,
  Link,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { z } from "zod";
import type { CampaignRouteContext } from "@/lib/types/route-context";
import { useDateFilter } from "@/lib/hooks/use-date-filter";
import type { DateRange } from "@/lib/types/date-filter";
import { DateRangeOption } from "@/lib/types/date-filter";
import { dateRangeToApiParams } from "@/lib/utils/date-range";
import { useMemo } from "react";

export const promoCodeSearchSchema = z.object({
  tab: z.enum(["overview", "rewards", "payments"]).optional().catch(undefined),
  reward: z.coerce.number().optional(),
});

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/promo-codes/$interactionId",
)({
  component: PromoCodesPage,
  validateSearch: promoCodeSearchSchema,
});

// ============================================================================
// Presentational View (exported for Storybook)
// ============================================================================

export interface PromoCodesPageViewProps {
  isLoading: boolean;
  error: Error | null;
  interactionNotFound: boolean;
  interactionTitle: string;
  secureUrl: string;
  offerPending: boolean;
  offerError: Error | null;
  offer: AffiliateOffer | undefined;
  organizationId: string;
  orgSlug: string;
  profileSlug: string;
  campaignId: string;
  interactionId: string;
  locationPathname: string;
  activeTab: string;
  onTabChange: (value: string) => void;
  reward: number | undefined;
  onRewardChange: (boostId: number | undefined) => void;
  overviewDateFilterSettings: {
    selectedOption: DateRangeOption;
    dateRange: DateRange;
    onChange: (option: DateRangeOption, customRange?: DateRange) => void;
  };
  paymentsDateFilterSettings: {
    selectedOption: DateRangeOption;
    dateRange: DateRange;
    onChange: (option: DateRangeOption, customRange?: DateRange) => void;
  };
}

export function PromoCodesPageView({
  isLoading,
  error,
  interactionNotFound,
  interactionTitle,
  secureUrl,
  offerPending,
  offerError,
  offer,
  organizationId,
  orgSlug,
  profileSlug,
  campaignId,
  interactionId,
  locationPathname,
  activeTab,
  onTabChange,
  reward,
  onRewardChange,
  overviewDateFilterSettings,
  paymentsDateFilterSettings,
}: PromoCodesPageViewProps): ReactNode {
  if (isLoading) {
    return <PromoCodesPageSkeleton />;
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:p-6 w-full">
        <ProjectPageHeader title="Promo Codes" />
        <div className="mt-4 py-8 text-center">
          <p className="text-destructive">
            Failed to load promo code details. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (interactionNotFound) {
    return (
      <div className="px-4 py-6 sm:p-6 w-full">
        <ProjectPageHeader title="Promo Codes" />
        <div className="mt-4 py-8 text-center">
          <p className="text-muted-foreground">
            Promo code not found. It may have been removed or you don&apos;t
            have access.
          </p>
        </div>
      </div>
    );
  }

  const previewLink =
    offer?.affiliateShortLink ||
    (offer && isPromoOffer(offer)
      ? (offer.linkedAffiliateLink?.affiliateShortLink ?? null)
      : null);

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
        <ProjectPageHeader
          title={interactionTitle}
          className="mb-0"
          subtitle={
            offer && (
              <span className="flex items-center gap-1">
                <Building2 className="inline h-3 w-3 align-[-2px]" />
                {offer.clientName} &middot; {offer.offerName}
              </span>
            )
          }
        />
      </div>

      {/* Content */}
      <div className="flex-1 w-full px-4 py-4 sm:px-6 sm:py-6">
        {/* Landing Page banner */}
        {offer?.isLandingPageEnabled && (
          <Card className="bg-primary/4 mb-4">
            <CardContent className="px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                  <Store className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Landing Page</p>
                  <p className="text-xs text-muted-foreground hidden sm:block">
                    Personalize your storefront and share it with your audience
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" asChild>
                    <Link
                      to="/o/$orgSlug/p/$profileSlug/c/$campaignId/customize-landing/$interactionId"
                      params={{
                        orgSlug,
                        profileSlug,
                        campaignId,
                        interactionId,
                      }}
                    >
                      <Paintbrush className="h-3.5 w-3.5 mr-1.5" />
                      Customize
                    </Link>
                  </Button>
                  {previewLink && (
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={previewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        Preview
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading state — only show when we have a secureUrl to fetch */}
        {offerPending && secureUrl && (
          <div className="px-4 py-6 sm:px-6">
            <OfferLoadingSkeleton />
          </div>
        )}

        {/* No offer available (secureUrl is empty/null) */}
        {!secureUrl && !isLoading && (
          <div className="py-8 text-center">
            <p className="text-muted-foreground">
              This offer is not currently available.
            </p>
          </div>
        )}

        {/* Error state */}
        {offerError && (
          <div className="py-12 text-center px-4">
            <p className="text-destructive">
              Failed to load offer details. Please try again.
            </p>
          </div>
        )}

        {/* Offer content with tabs */}
        {offer && (
          <Tabs
            value={activeTab}
            onValueChange={onTabChange}
            className="w-full"
          >
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {offer.boosts.length > 0 && (
                <TabsTrigger value="rewards">Rewards</TabsTrigger>
              )}
              <TabsTrigger value="payments">Payments</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0 space-y-4">
              <OfferStatsDisplay
                stats={offer.stats}
                showClicks={isLinkOffer(offer) || isPromoOffer(offer)}
                offerCreatedDate={offer.createdDate}
                isLoading={offerPending}
                dateFilter={overviewDateFilterSettings}
              />

              {isPromoOffer(offer) && (
                <PromoCodeCard
                  offer={offer}
                  linkedAffiliateLink={offer.linkedAffiliateLink}
                  organizationId={organizationId}
                  offerUrl={secureUrl}
                />
              )}
              {isLinkOffer(offer) && (
                <AffiliateLinkCard
                  offer={offer}
                  organizationId={organizationId}
                  offerUrl={secureUrl}
                />
              )}

              {/* Share button */}
              <div className="flex justify-center">
                <ShareOfferDialog
                  offer={offer}
                  socialLinks={offer.socialLinks}
                />
              </div>

              <PayoutTierDetails
                payoutTiers={offer.payoutTiers}
                offerAutomationConfig={offer.offerAutomationConfig}
                stats={offer.stats}
                showPayoutsOnCreatorPortal={offer.showPayoutsOnCreatorPortal}
              />
            </TabsContent>

            {offer.boosts.length > 0 && (
              <TabsContent value="rewards" className="mt-0">
                <BoostRewardsSection
                  boosts={offer.boosts}
                  memberId={offer.memberId}
                  orgSlug={orgSlug}
                  profileSlug={profileSlug}
                  campaignId={`${campaignId}`}
                  interactionId={interactionId}
                  interactionRoute={locationPathname}
                  selectedBoostId={reward}
                  onSelectedBoostChange={onRewardChange}
                />
              </TabsContent>
            )}

            <TabsContent value="payments" className="mt-0">
              <PaymentsTab
                payments={offer.payments}
                stats={offer.stats}
                offerCreatedDate={offer.createdDate}
                isLoading={offerPending}
                dateFilter={paymentsDateFilterSettings}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Container (hooks + data fetching)
// ============================================================================

function PromoCodesPage() {
  const { interactionId } = Route.useParams();
  const { tab, reward } = Route.useSearch();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { campaign, programId, profile, organization } = context;
  const location = useLocation();
  const navigate = useNavigate({ from: Route.fullPath });
  const currentTab = tab ?? "overview";

  // Get interactions for this project to find the secureUrl
  const {
    data: interactionsData,
    isPending: interactionsPending,
    error: interactionsError,
  } = useProjectInteractionsQuery(organization.id, programId, profile.id);

  // Find the specific interaction by ID
  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );

  // Date filter state (independent per tab)
  const createdDate = interaction?.createdDate
    ? new Date(interaction.createdDate)
    : undefined;
  const overviewDateFilter = useDateFilter({
    offerCreatedDate: createdDate,
  });
  const paymentsDateFilter = useDateFilter({
    offerCreatedDate: createdDate,
  });

  type TabValue = "overview" | "rewards" | "payments";
  const handleTabChange = (value: string) => {
    const nextTab = value as TabValue;

    // Reset each section to All Time whenever user switches into it.
    if (nextTab !== currentTab) {
      if (nextTab === "overview") {
        overviewDateFilter.onChange(DateRangeOption.ALL_TIME);
      }
      if (nextTab === "payments") {
        paymentsDateFilter.onChange(DateRangeOption.ALL_TIME);
      }
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        tab: nextTab,
        // Clear reward when switching away from rewards tab
        reward: value === "rewards" ? prev.reward : undefined,
      }),
      replace: true,
    });
  };

  const handleRewardChange = (boostId: number | undefined) => {
    void navigate({
      search: (prev) => ({ ...prev, reward: boostId }),
      replace: true,
    });
  };

  const secureUrl = interaction?.secureUrl ?? "";

  // Convert date range to API parameters
  const activeDateRange =
    currentTab === "payments"
      ? paymentsDateFilter.dateRange
      : overviewDateFilter.dateRange;
  const dateParams = useMemo(
    () => dateRangeToApiParams(activeDateRange),
    [activeDateRange],
  );

  // Fetch affiliate offer data using the secureUrl with date filters
  const {
    data: offerData,
    isPending: offerPending,
    error: offerError,
  } = useAffiliateOfferQuery(organization.id, secureUrl, dateParams);

  const offer = offerData?.offer;
  const hasBoosts = (offer?.boosts.length ?? 0) > 0;
  // Fall back to "overview" if rewards tab is requested but no boosts exist
  const activeTab =
    tab === "rewards" && !hasBoosts ? "overview" : (tab ?? "overview");

  return (
    <PromoCodesPageView
      isLoading={interactionsPending}
      error={interactionsError}
      interactionNotFound={!interactionsPending && !interaction}
      interactionTitle={interaction?.title ?? "Promo Codes"}
      secureUrl={secureUrl}
      offerPending={offerPending}
      offerError={offerError}
      offer={offer}
      organizationId={organization.id}
      orgSlug={organization.slug}
      profileSlug={campaign.profileSlug}
      campaignId={String(programId)}
      interactionId={interactionId}
      locationPathname={location.pathname}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      reward={reward}
      onRewardChange={handleRewardChange}
      overviewDateFilterSettings={{
        selectedOption: overviewDateFilter.selectedOption,
        dateRange: overviewDateFilter.dateRange,
        onChange: overviewDateFilter.onChange,
      }}
      paymentsDateFilterSettings={{
        selectedOption: paymentsDateFilter.selectedOption,
        dateRange: paymentsDateFilter.dateRange,
        onChange: paymentsDateFilter.onChange,
      }}
    />
  );
}

function PromoCodesPageSkeleton() {
  return (
    <div className="flex flex-col min-h-full">
      <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Skeleton className="h-5 w-40 mb-1.5" />
            <Skeleton className="h-3.5 w-56" />
          </div>
        </div>
      </div>
      <div className="px-4 py-6 sm:px-6 w-full">
        <OfferLoadingSkeleton />
      </div>
    </div>
  );
}

function OfferLoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div>
        <Skeleton className="h-5 w-32 mb-3" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-4 w-48 mx-auto" />
      </div>

      <div className="flex justify-center">
        <Skeleton className="h-10 w-36" />
      </div>

      <Separator />

      <div>
        <Skeleton className="h-4 w-20 mb-3" />
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}
